import ActivateIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UserIcon from '@mui/icons-material/PersonRounded';
import AdminIcon from '@mui/icons-material/SupervisorAccountRounded';
import { Box, IconButton, Menu, MenuItem as MUIMenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import { OrganizationRole } from '@/entities/session';
import { useConfirmationDialog } from '@/shared/hooks';
import { useAppDispatch } from '@/shared/model';
import { Button } from '@/shared/ui';
import { updateUser } from '../../model/slice';
import type { User } from '../../model/types';
import { DeactivateUserIcon } from './DeactivateUserIcon';

const MenuItem = styled(MUIMenuItem)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
}));

export enum MenuButtonType {
  ICON = 'icon',
  BUTTON = 'button',
}

interface Props {
  type?: MenuButtonType;
  user: User;
}

export function UserActionsMenu({ type = MenuButtonType.ICON, user }: Props) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, dialogElememnt } = useConfirmationDialog({
    slotProps: {
      dialog: {
        sx: {
          width: 370,
        },
      },
    },
    title: 'Deactivate',
    description: (
      <>
        If you deactivate the user, they will lose access to their account until
        it is reactivated.
        <Box component="p" sx={{ mt: 2, mb: 0, fontWeight: 500 }}>
          Are you sure you want to do this?
        </Box>
      </>
    ),
    acceptButtonColor: 'error',
    acceptButtonText: 'Deactivate',
    onAccept: async () => {
      try {
        await dispatch(
          updateUser({ user, update: { isActive: false } }),
        ).unwrap();
      } catch (error) {
        enqueueSnackbar('Unable to update the user.', {
          variant: 'error',
          autoHideDuration: 4000,
        });

        throw error;
      }
    },
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleUserStatusChange = () => {
    if (user.isActive) {
      openDialog();
    } else {
      dispatch(updateUser({ user, update: { isActive: true } }));
    }
  };

  const handleRoleChange = (newRole: OrganizationRole) => () => {
    dispatch(updateUser({ user, update: { role: newRole } }));
  };

  return (
    <>
      {type === MenuButtonType.ICON && (
        <IconButton sx={{ p: 0 }} size="small" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
      )}
      {type === MenuButtonType.BUTTON && (
        <Button
          sx={{ py: 1.125 }}
          variant="outlined"
          color="secondary"
          startIcon={<MoreVertIcon />}
          onClick={handleClick}
        >
          More
        </Button>
      )}
      <Menu
        MenuListProps={{
          sx: {
            p: 0,
            borderRadius: 2,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
        anchorEl={anchorEl}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem sx={{ py: 0.75, color: 'text.primary' }}>
          {user.role === OrganizationRole.USER ? (
            <Box
              sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
              onClick={handleRoleChange(OrganizationRole.ADMIN)}
            >
              <AdminIcon sx={{ color: '#7E828F' }} />
              Change to Admin
            </Box>
          ) : (
            <Box
              sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
              onClick={handleRoleChange(OrganizationRole.USER)}
            >
              <UserIcon sx={{ color: '#7E828F' }} />
              Change to User
            </Box>
          )}
        </MenuItem>
        <MenuItem
          sx={{ py: 0.75, color: 'text.primary' }}
          onClick={handleUserStatusChange}
        >
          {user.isActive ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DeactivateUserIcon />
              Deactivate
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ActivateIcon sx={{ color: '#7E828F' }} />
              Activate
            </Box>
          )}
        </MenuItem>
      </Menu>
      {dialogElememnt}
    </>
  );
}
